import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { backend } from '@/api/backend';
import { scopeLabel } from '@/pages/registration';
import { ApiError } from '@/api/http';
import { explainLine } from '@/api/explain';
import { BucketSearch } from '@/components/BucketSearch';
import { Loading } from '@/components/ui';
import { STAGE_ORDER, TITLES } from '@/pages/workflow';

const retry = (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2);

/**
 * The entry to the product: choose the bucket you are working in.
 *
 * The landing page used to be a catalogue - every registered pipeline listed,
 * every company under it - and the rail repeated the same two lists beside
 * every screen. That is a directory, and it answers a question nobody has:
 * an operator arrives knowing which corpus they are working on, and wants to
 * be in it. It also does not survive its own scale, since a list of pipelines
 * is a list of a thing there may be a hundred thousand of.
 *
 * So there is one question here, and the rest of the product is the answer to
 * it. Everything downstream is that bucket's, and the same control in the rail
 * switches to another.
 */
export function ChooseBucket() {
  const navigate = useNavigate();
  const registered = useQuery({
    queryKey: ['live-buckets'],
    queryFn: ({ signal }) => backend.listAllBuckets(signal),
    retry,
    staleTime: 30_000,
  });

  if (registered.isPending) return <Loading what="Reading the buckets you can work on" rows={3} />;

  if (registered.isError)
    return (
      <div className="min-h-0 flex-1 p-5">
        <p role="alert" className="flex flex-wrap items-center gap-2 text-small text-warn">
          <span className="min-w-0 flex-1">
            {explainLine(registered.error, 'read the buckets you can work on')}
          </span>
          <button
            type="button"
            onClick={() => {
              void registered.refetch();
            }}
            className="flex-none rounded border border-line bg-panel px-2.5 py-1 text-acc-2 transition-colors duration-fast hover:bg-hov"
          >
            Try again
          </button>
        </p>
      </div>
    );

  const buckets = registered.data?.items ?? [];
  /**
   * The most recently registered scopes, as a way in for someone with no name
   * in mind. NOT the ones with work outstanding.
   *
   * This sat under a heading reading "Already running", and the catalogue
   * carries nothing that could support that: an item is `{bucket, accountId,
   * prefix, sweepSelected}` and no field in it names a run or a stage. Three
   * of the four buckets it listed were DELIVERED and finished, while anasoft -
   * the only one genuinely part-built - looked exactly the same as them.
   *
   * That is the claim most likely to make an operator think switching buckets
   * lost their work: if the list of what is "running" is really the list of
   * what exists, it tells you nothing about where to go back to. Saying what
   * this actually is costs one word; saying what is running needs a field the
   * backend does not return yet, which is ask 10.
   */
  const recent = buckets.slice(0, 5);

  return (
    <div className="grid min-h-0 flex-1 place-items-center overflow-y-auto p-5">
      <div className="w-full max-w-xl animate-pop">
        <h1 className="text-h2 text-ink">Which bucket are you working on?</h1>
        <p className="mt-1 text-small text-ink-2">
          Search every bucket in the account, registered or not. Choosing one that has never been registered starts its
          run from the beginning. Everything after this is scoped to what you pick, and the same box in the rail
          switches.
        </p>

        <div className="mt-4">
          <BucketSearch focusOnMount />
        </div>

        {recent.length > 0 && (
          <div className="mt-6">
            <p className="text-caption uppercase tracking-wider text-ink-4">Recently registered</p>
            <div className="mt-1.5 overflow-hidden rounded-md border border-line bg-panel">
              {recent.map((b) => (
                <button
                  key={b.bucket.id}
                  type="button"
                  onClick={() => navigate(`/buckets/${b.bucket.id}`)}
                  className="flex w-full items-center gap-3 border-b border-line px-4 py-2.5 text-left transition-colors duration-fast last:border-0 hover:bg-hov"
                >
                  {/* The name. Region and account address the storage; they
                      are not what someone is choosing between. */}
                  <span className="mono min-w-0 flex-1 truncate text-row text-ink">{scopeLabel(b.bucket.name, (b as { prefix?: string }).prefix ?? '')}</span>
                  <ChevronRight className="h-3.5 w-3.5 flex-none text-ink-4" aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* The shape of the work, once, where someone new can read it - rather
            than repeated beside every pipeline in a list. */}
        <ol className="mt-6 flex flex-wrap items-center gap-x-1.5 gap-y-1">
          {STAGE_ORDER.map((id, i) => (
            <li key={id} className="flex items-center gap-1.5">
              <span className="mono text-caption text-ink-4">{i + 1}</span>
              <span className="text-caption text-ink-3">{TITLES[id].title}</span>
              {i < STAGE_ORDER.length - 1 && <ChevronRight className="h-3 w-3 text-ink-4" aria-hidden="true" />}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
